// LOGÍSTICA › Despacho de una nota. Por línea: solicitado, reservado, despachado y pendiente (cifras del
// backend). Se elige la ubicación de origen ERP (BODEGA / MESÓN / CAMIÓN) y la cantidad; se PREPARA el
// despacho (borrador) y, con permiso, se confirma la salida. Soporta despacho PARCIAL (10 → 6, quedan 4)
// y varios despachos por nota (#1, #2…): el saldo se vuelve a proponer. El stock, la reserva, el
// movimiento, el costo y la regla D-132 los decide el backend; aquí solo se presenta su respuesta.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/cliente';
import {
  claves,
  useCumplimiento,
  useDespachosDeNota,
  useDetallesDespacho,
  useNota,
  useUbicaciones,
  usePagoNota,
} from '../api/consultas';
import {
  MODALIDADES_DESPACHO,
  UBICACIONES_DE_VENTA,
  type Despacho as DespachoApi,
  type DespachoCreado,
  type ModalidadDespacho,
} from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import {
  Cumplimiento,
  EstadoNota,
  EstadoOperacion,
  PanelError,
  type Operacion,
} from '../comun/Estados';
import { EstadoPago } from '../comun/EstadoPago';
import { Modal } from '../comun/Modal';
import { decimal, entradaDecimal, esCero, fechaHora, sinCerosDecimales } from '../comun/formato';
import { EstadoDteDocumento, PanelDocumentoTributario } from '../dte/EstadoDte';
import { presentarError } from '../errores/catalogo';
import { useSesion } from '../sesion/SesionContexto';
import { GuiaInternaDe } from '../ventas/GuiaInterna';
import { useNombreActor } from '../ventas/Paneles';

const CODIGOS_D132 = [
  'DTE_NO_ENVIADO_ANTES_DEL_TRASLADO',
  'TRASLADO_SIN_DTE',
  'DTE_INTERACTIVO_TRASLADO_PENDIENTE',
];

/** D-132 en palabras: estado DTE, folio y TrackID (si el usuario puede verlos) y el código de soporte. */
export function SalidaRetenida({ error }: { error: unknown }) {
  const { empresa, puede } = useSesion();
  const p = presentarError(error);
  if (!CODIGOS_D132.includes(p.codigo)) return null;
  const dteId =
    typeof p.detalle['dteDocumentoId'] === 'string' ? p.detalle['dteDocumentoId'] : null;
  return (
    <div className="salida-retenida" data-salida-retenida={p.codigo}>
      <dl className="detalle-tecnico">
        {p.detalle['lineaNumero'] !== undefined && (
          <>
            <dt>Línea</dt>
            <dd>{String(p.detalle['lineaNumero'])}</dd>
          </>
        )}
        {p.codigo === 'TRASLADO_SIN_DTE' && (
          <>
            <dt>Documento tributario</dt>
            <dd>La línea no tiene documento tributario (solicítelo desde la nota).</dd>
          </>
        )}
        {p.detalle['estado'] !== undefined && (
          <>
            <dt>Estado DTE</dt>
            <dd>
              {String(p.detalle['estado'] ?? '—')} · envío {String(p.detalle['estadoEnvio'] ?? '—')}{' '}
              · SII {String(p.detalle['estadoSii'] ?? '—')}
            </dd>
          </>
        )}
        <dt>Código de soporte</dt>
        <dd>
          <code>{p.codigo}</code>
          {p.correlacionId && (
            <>
              {' '}
              · correlación <code>{p.correlacionId}</code>
            </>
          )}
        </dd>
      </dl>
      {dteId && puede('dte.ver') && (
        <EstadoDteDocumento empresaId={empresa!.id} documentoId={dteId} />
      )}
    </div>
  );
}

function LineasDespacho({ d }: { d: DespachoApi | undefined }) {
  if (!d?.lineas) return <span className="apagado">…</span>;
  return (
    <span className="mono">
      {d.lineas.map((l) => `L${l.numero} ${decimal(l.cantidad_base)}`).join(' · ')}
    </span>
  );
}

export function Despacho({ notaId, activa = true }: { notaId: string; activa?: boolean }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const nota = useNota(empresaId, notaId);
  const cumpl = useCumplimiento(empresaId, notaId);
  // Mesón: bodega y despacho ven si la nota ya está pagada (recibos de caja).
  const pago = usePagoNota(empresaId, notaId, puede('caja.recibo.ver'));
  const despachos = useDespachosDeNota(empresaId, notaId);
  const lista = useMemo(
    () => [...(despachos.data?.items ?? [])].sort((a, b) => a.creado_en.localeCompare(b.creado_en)),
    [despachos.data],
  );
  const detalles = useDetallesDespacho(
    empresaId,
    lista.map((d) => d.id),
  );
  const ubicaciones = useUbicaciones(empresaId);
  const actor = useNombreActor();
  const intentos = useIntentos();
  const origenes = useMemo(
    () => (ubicaciones.data ?? []).filter((u) => u.tipo && UBICACIONES_DE_VENTA.includes(u.tipo)),
    [ubicaciones.data],
  );
  const [ubicacionId, setUbicacionId] = useState('');
  const [modalidad, setModalidad] = useState<ModalidadDespacho | ''>('');
  const [cantidades, setCantidades] = useState<Record<number, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [op, setOp] = useState<Operacion>('listo');
  const [confirmar, setConfirmar] = useState<{
    id: string;
    version: number;
    numero: string;
  } | null>(null);
  const [descartar, setDescartar] = useState<{
    id: string;
    version: number;
    numero: string;
  } | null>(null);
  const [motivo, setMotivo] = useState('');
  /** Sale con guía interna (opción A 2026-10-23): lo que se factura después, como en Random. */
  const [conGuia, setConGuia] = useState(false);
  const [guiaDe, setGuiaDe] = useState<string | null>(null);
  const primerInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!ubicacionId && nota.data) setUbicacionId(nota.data.cabecera.ubicacionId);
  }, [nota.data, ubicacionId]);
  // Por omisión se propone despachar lo RESERVADO vigente de cada línea (cifra del backend): después
  // de un despacho parcial, lo reservado es el saldo, y eso es lo que se vuelve a proponer.
  useEffect(() => {
    if (cumpl.data)
      setCantidades(
        Object.fromEntries(
          cumpl.data.lineas.map((l) => [
            l.numero,
            esCero(l.reservado) ? '' : sinCerosDecimales(l.reservado),
          ]),
        ),
      );
  }, [cumpl.data]);
  useEffect(() => {
    if (activa && cumpl.data) primerInput.current?.focus();
  }, [activa, cumpl.data]);

  const invalidar = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: claves.cumplimiento(empresaId, notaId) }),
      qc.invalidateQueries({ queryKey: claves.despachos(empresaId, notaId) }),
      qc.invalidateQueries({ queryKey: ['emp', empresaId, 'despacho'] }),
      qc.invalidateQueries({ queryKey: claves.nota(empresaId, notaId) }),
      qc.invalidateQueries({ queryKey: ['emp', empresaId, 'disponibilidad'] }),
    ]);

  const preparar = useMutation({
    mutationFn: () => {
      const lineas = Object.entries(cantidades)
        .filter(([, v]) => v.trim())
        .map(([n, v]) => ({
          lineaNumero: Number.parseInt(n, 10),
          cantidadBase: entradaDecimal(v, 4) ?? v,
        }));
      const cuerpo = {
        notaId,
        ubicacionId,
        ...(modalidad ? { modalidad } : {}),
        ...(lineas.length ? { lineas } : {}),
      };
      return api.post<DespachoCreado>('/ventas/despachos', cuerpo, {
        idempotencia: intentos.clave('crear-despacho', cuerpo),
      });
    },
    onMutate: () => {
      setError(null);
      setOp('guardando');
    },
    onSuccess: async () => {
      intentos.cerrar();
      await invalidar();
      setOp('listo');
    },
    onError: (e) => {
      intentos.cerrar(e);
      setError(e);
      setOp('error');
    },
  });

  const transicion = useMutation({
    mutationFn: (v: {
      id: string;
      version: number;
      accion: 'confirmar' | 'descartar';
      motivo?: string;
    }) => {
      const cuerpo = {
        version: v.version,
        ...(v.motivo ? { motivo: v.motivo } : {}),
        ...(v.accion === 'confirmar' && conGuia ? { guiaInterna: true } : {}),
      };
      return api.post(`/ventas/despachos/${v.id}/${v.accion}`, cuerpo, {
        idempotencia: intentos.clave(`despacho-${v.accion}`, { id: v.id, cuerpo }),
      });
    },
    onMutate: () => setOp('procesando'),
    onSuccess: async (_r, v) => {
      intentos.cerrar();
      // Con guía interna: al salir, se emite e imprime la guía (patente y chofer).
      if (v.accion === 'confirmar' && conGuia) setGuiaDe(v.id);
      setConGuia(false);
      setConfirmar(null);
      setDescartar(null);
      setMotivo('');
      await invalidar();
      setOp('listo');
    },
    onError: (e) => {
      intentos.cerrar(e);
      setOp('error');
    },
  });

  if (nota.isError) return <PanelError error={nota.error} />;
  if (!nota.data || !cumpl.data) return <EstadoOperacion op="cargando" />;
  const n = nota.data;
  const borradores = lista.filter((d) => d.estado === 'BORRADOR');
  const estados = cumpl.data.lineas.map((l) => l.estado);
  const resumen = estados.every((e) => e === 'COMPLETA')
    ? 'COMPLETA'
    : estados.every((e) => e === 'SIN_DESPACHO')
      ? 'SIN_DESPACHO'
      : 'PARCIAL';
  const detalleDe = (id: string) => detalles[lista.findIndex((d) => d.id === id)]?.data;

  return (
    <div className="pantalla despacho" data-pantalla="despacho">
      <div className="doc-titulo">
        <h2>Despacho · {n.numeroVisible}</h2>
        <EstadoNota estado={n.estado} />
        <span data-cumplimiento={resumen}>
          <Cumplimiento estado={resumen} />
        </span>
        <EstadoPago pago={pago.data} />
        <span>
          {n.cabecera.clienteRazonSocial} · {n.cabecera.documentoSolicitado}
        </span>
        <span className="espaciador" />
        <EstadoOperacion op={op} />
      </div>

      <div className="cabecera-doc">
        <label className="campo">
          <span className="etiqueta">Ubicación de origen</span>
          <select
            aria-label="Ubicación de origen"
            value={ubicacionId}
            onChange={(e) => setUbicacionId(e.target.value)}
          >
            {origenes.map((u) => (
              <option key={u.id} value={u.id}>
                {u.codigo} · {u.nombre} ({u.tipo})
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Modalidad</span>
          <select
            aria-label="Modalidad"
            value={modalidad}
            onChange={(e) => setModalidad(e.target.value as ModalidadDespacho | '')}
          >
            <option value="">Según ubicación y canal</option>
            {MODALIDADES_DESPACHO.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <p className="ayuda">
          RUTA sale de un CAMIÓN; RETIRO es el mesón; DESPACHO es reparto desde bodega. La cantidad
          propuesta es lo reservado vigente de cada línea: cambie la cifra para un despacho parcial.
        </p>
      </div>

      <table className="grilla" aria-label="Líneas a despachar">
        <thead>
          <tr>
            <th>#</th>
            <th>Producto</th>
            <th className="al-der">Solicitado</th>
            <th className="al-der">Reservado</th>
            <th className="al-der">Despachado</th>
            <th className="al-der">Pendiente</th>
            <th>Estado</th>
            <th className="al-der" style={{ width: '8rem' }}>
              A despachar (u. base)
            </th>
          </tr>
        </thead>
        <tbody>
          {cumpl.data.lineas.map((l, i) => {
            const ln = n.lineas.find((x) => x.numero === l.numero);
            return (
              <tr key={l.lineaId} data-despacho-linea={l.numero}>
                <td className="mono">{l.numero}</td>
                <td>{ln ? `${ln.sku} · ${ln.descripcion}` : l.productoId}</td>
                <td className="al-der mono" data-col="solicitado">
                  {decimal(l.confirmado)}
                </td>
                <td className="al-der mono fuerte" data-col="reservado">
                  {decimal(l.reservado)}
                </td>
                <td className="al-der mono" data-col="despachado">
                  {decimal(l.despachado)}
                </td>
                <td
                  className={`al-der mono ${esCero(l.pendienteDespacho) ? '' : 'texto-aviso fuerte'}`}
                  data-col="pendiente"
                >
                  {decimal(l.pendienteDespacho)}
                </td>
                <td>
                  <Cumplimiento estado={l.estado} />
                </td>
                <td className="al-der">
                  <input
                    ref={i === 0 ? primerInput : undefined}
                    className="celda-num"
                    inputMode="decimal"
                    aria-label={`A despachar línea ${l.numero}`}
                    value={cantidades[l.numero] ?? ''}
                    disabled={esCero(l.pendienteDespacho)}
                    onChange={(e) => setCantidades((c) => ({ ...c, [l.numero]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        !preparar.isPending &&
                        puede('ventas.despacho.crear')
                      ) {
                        e.preventDefault();
                        preparar.mutate();
                      }
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {!!error && (
        <PanelError error={error} alCerrar={() => setError(null)}>
          <SalidaRetenida error={error} />
        </PanelError>
      )}

      <div className="acciones">
        {puede('ventas.despacho.crear') ? (
          <button
            type="button"
            className="primario"
            disabled={
              preparar.isPending ||
              !ubicacionId ||
              n.estado !== 'CONFIRMADA' ||
              resumen === 'COMPLETA'
            }
            onClick={() => preparar.mutate()}
          >
            {preparar.isPending ? 'Preparando…' : 'Preparar despacho (Enter)'}
          </button>
        ) : (
          <span className="apagado">Su usuario no puede preparar despachos.</span>
        )}
        {resumen === 'COMPLETA' && (
          <span className="insignia tono-ok">Nota despachada completa</span>
        )}
      </div>

      <section className="panel" aria-label="Despachos de la nota">
        <h3>Historial de despachos</h3>
        <table className="grilla compacta">
          <thead>
            <tr>
              <th>N°</th>
              <th>Estado</th>
              <th>Cantidades (u. base)</th>
              <th>Origen</th>
              <th>Modalidad</th>
              <th>Preparado</th>
              <th>Salida confirmada</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lista.map((d, i) => (
              <tr key={d.id} data-despacho={d.estado} data-despacho-n={i + 1}>
                <td className="mono">
                  Despacho #{i + 1} <span className="apagado">(N° {d.numero})</span>
                </td>
                <td>
                  <span
                    className={`insignia tono-${d.estado === 'CONFIRMADO' ? 'ok' : d.estado === 'BORRADOR' ? 'neutro' : 'apagado'}`}
                  >
                    {d.estado}
                  </span>
                </td>
                <td>
                  <LineasDespacho d={detalleDe(d.id)} />
                </td>
                <td>{origenes.find((u) => u.id === d.ubicacion_id)?.codigo ?? '—'}</td>
                <td>{d.modalidad}</td>
                <td>
                  {fechaHora(d.creado_en)} · {actor(d.creado_por)}
                </td>
                <td>
                  {d.confirmado_en
                    ? `${fechaHora(d.confirmado_en)} · ${actor(d.confirmado_por)}`
                    : '—'}
                </td>
                <td>
                  <div className="acciones-linea">
                    {d.estado === 'BORRADOR' && puede('ventas.despacho.confirmar') && (
                      <button
                        type="button"
                        className="boton-mini"
                        onClick={() => {
                          transicion.reset();
                          setConfirmar({ id: d.id, version: d.version, numero: `#${i + 1}` });
                        }}
                      >
                        Confirmar salida
                      </button>
                    )}
                    {d.estado === 'CONFIRMADO' && (
                      <button type="button" className="boton-mini" onClick={() => setGuiaDe(d.id)}>
                        Guía interna
                      </button>
                    )}
                    {d.estado === 'BORRADOR' && puede('ventas.despacho.crear') && (
                      <button
                        type="button"
                        className="boton-mini peligro"
                        onClick={() => {
                          transicion.reset();
                          setDescartar({ id: d.id, version: d.version, numero: `#${i + 1}` });
                        }}
                      >
                        Descartar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {despachos.data && !lista.length && (
              <tr>
                <td colSpan={8} className="grilla-vacia">
                  Sin despachos todavía
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {borradores.length > 0 && !puede('ventas.despacho.confirmar') && (
          <p className="ayuda">
            Hay despachos preparados: los confirma quien tenga permiso de confirmar salida.
          </p>
        )}
      </section>

      <PanelDocumentoTributario nota={n} />

      {confirmar && (
        <Modal
          titulo={`Confirmar salida · despacho ${confirmar.numero}`}
          ancho="medio"
          alCerrar={() => !transicion.isPending && setConfirmar(null)}
          pie={
            <>
              <button
                type="button"
                onClick={() => setConfirmar(null)}
                disabled={transicion.isPending}
              >
                Volver
              </button>
              <button
                type="button"
                className="primario"
                data-autofoco
                disabled={transicion.isPending}
                onClick={() => transicion.mutate({ ...confirmar, accion: 'confirmar' })}
              >
                {transicion.isPending ? 'Procesando…' : 'Confirmar salida'}
              </button>
            </>
          }
        >
          <p>
            La salida mueve el stock físico y consume la reserva. Si el documento tributario todavía
            no ampara el traslado, el backend la retiene y la mercadería no sale.
          </p>
          {puede('ventas.guia_interna.emitir') && (
            <label className="opcion-linea">
              <input
                type="checkbox"
                checked={conGuia}
                onChange={(e) => setConGuia(e.target.checked)}
              />{' '}
              <span>
                <b>Sale con guía interna</b> (se factura después, como en Random). Al confirmar se
                imprime la guía con su folio GDI.
              </span>
            </label>
          )}
          {!!transicion.error && (
            <PanelError error={transicion.error}>
              <SalidaRetenida error={transicion.error} />
            </PanelError>
          )}
        </Modal>
      )}
      {guiaDe && (
        <GuiaInternaDe origen={{ despachoId: guiaDe }} alTerminar={() => setGuiaDe(null)} />
      )}
      {descartar && (
        <Modal
          titulo={`Descartar despacho ${descartar.numero}`}
          ancho="angosto"
          alCerrar={() => !transicion.isPending && setDescartar(null)}
          pie={
            <>
              <button type="button" onClick={() => setDescartar(null)}>
                Volver
              </button>
              <button
                type="button"
                className="peligro"
                disabled={transicion.isPending || motivo.trim().length < 3}
                onClick={() =>
                  transicion.mutate({ ...descartar, accion: 'descartar', motivo: motivo.trim() })
                }
              >
                Descartar
              </button>
            </>
          }
        >
          <label className="campo">
            Motivo (obligatorio)
            <input data-autofoco value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </label>
          {!!transicion.error && <PanelError error={transicion.error} />}
        </Modal>
      )}
    </div>
  );
}
